import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '~/hooks/useAuth';

/**
 * OAuth callback landing page.
 *
 * Supabase's `detectSessionInUrl: true` (set in supabase.ts) automatically
 * detects the PKCE authorization code (or implicit-flow hash tokens) and
 * exchanges them for a session before any component code runs.  By the time
 * this component mounts, the URL has already been cleaned and the session
 * is either established or in the process of being established.
 *
 * All we need to do here is wait for the AuthProvider to finish resolving
 * and then redirect accordingly.
 */
export const AuthCallback = () => {
  const navigate = useNavigate();
  const { user, loading, error } = useAuth();

  useEffect(() => {
    console.log('[AuthCallback] useEffect', { loading, hasUser: !!user, error, url: window.location.href });

    // Check for an OAuth error passed back in the query string
    const params = new URLSearchParams(window.location.search);
    const oauthError = params.get('error_description') || params.get('error');
    if (oauthError) {
      console.log('[AuthCallback] OAuth error in URL:', oauthError);
      navigate('/login', { replace: true });
      return;
    }

    // Wait until AuthProvider finishes loading
    if (loading) {
      console.log('[AuthCallback] still loading, waiting...');
      return;
    }

    if (user) {
      console.log('[AuthCallback] user found, navigating to /');
      navigate('/', { replace: true });
    } else {
      console.log('[AuthCallback] no user after loading, navigating to /login');
      navigate('/login', { replace: true });
    }
  }, [user, loading, error, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        {error ? (
          <>
            <p className="text-red-700 font-medium">Sign in failed</p>
            <p className="mt-2 text-gray-600 text-sm">{error}</p>
            <p className="mt-2 text-gray-500 text-sm">Redirecting to login...</p>
          </>
        ) : (
          <>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Completing sign in...</p>
          </>
        )}
      </div>
    </div>
  );
};
